import { useState } from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SecondaryButton, GlassButton } from '@/components/ui/button-variants';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import AvatarUpload from './AvatarUpload';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { copyToClipboard } from '@/lib/utils';
import { Settings, MapPin, Link as LinkIcon, Calendar, Edit, Camera, Share, Check } from 'lucide-react';

interface Profile {
  id: string;
  username: string;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  website: string | null;
  location: string | null;
  created_at: string;
  posts_count: number;
  followers_count: number;
  following_count: number;
  pets_count: number;
}

interface ProfileHeaderProps {
  profile: Profile;
  isOwnProfile: boolean;
  onUpdateProfile?: (updates: Partial<Profile>) => Promise<{ success: boolean; error?: any }>;
  refetchProfile?: () => void;
}

const ProfileHeader = ({ profile, isOwnProfile, onUpdateProfile, refetchProfile }: ProfileHeaderProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [editForm, setEditForm] = useState({
    display_name: profile.display_name || '',
    bio: profile.bio || '',
    website: profile.website || '',
    location: profile.location || '',
  });

  const handleSaveProfile = async () => {
    if (!onUpdateProfile) return;

    const result = await onUpdateProfile(editForm);
    if (result.success) {
      setIsEditing(false);
    }
  };

  const handleShareProfile = async () => {
    const profileUrl = `${window.location.origin}/app/user/${profile.username}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Profilo di ${profile.display_name || profile.username}`,
          text: `Guarda il profilo di ${profile.display_name || profile.username} su AstroPet!`,
          url: profileUrl,
        });
      } catch (error) {
        // User cancelled sharing or error occurred
      }
    } else {
      // Fallback to clipboard
      const success = await copyToClipboard(profileUrl);
      if (success) {
        setIsSharing(true);
        toast({
          title: "Link copiato!",
          description: "Il link del profilo è stato copiato negli appunti",
        });
        setTimeout(() => setIsSharing(false), 2000);
      } else {
        toast({
          title: "Errore",
          description: "Impossibile copiare il link",
          variant: "destructive",
        });
      }
    }
  };

  const formatCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  return (
    <Card className="glass border-white/20 mb-6">
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Avatar */}
          <div className="flex justify-center md:justify-start">
            {isOwnProfile && user ? (
              <AvatarUpload
                currentAvatarUrl={profile.avatar_url}
                username={profile.username}
                userId={user.id}
                onAvatarUpdate={(avatarUrl) => {
                  if (refetchProfile) refetchProfile();
                }}
                size="lg"
              />
            ) : (
              <Avatar className="w-32 h-32 ring-4 ring-primary/30">
                <AvatarImage src={profile.avatar_url || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-bold">
                  {profile.username.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            )}
          </div>

          {/* Profile Info */}
          <div className="flex-1 space-y-4">
            {/* Header Row */}
            <div className="space-y-4">
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {profile.display_name || profile.username}
                </h1>
                <p className="text-muted-foreground">@{profile.username}</p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 gap-y-3 items-center justify-center md:justify-start">
                {isOwnProfile ? (
                  <>
                    <Dialog open={isEditing} onOpenChange={setIsEditing}>
                       <DialogTrigger asChild>
                         <SecondaryButton className="text-sm px-3 py-2 flex-shrink-0 whitespace-nowrap">
                           <Edit className="w-4 h-4 mr-2" />
                           <span className="hidden sm:inline">Modifica Profilo</span>
                           <span className="sm:hidden">Modifica</span>
                         </SecondaryButton>
                      </DialogTrigger>
                      <DialogContent className="glass border-white/20">
                        <DialogHeader>
                          <DialogTitle className="text-white">Modifica Profilo</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="display_name">Nome Visualizzato</Label>
                            <Input
                              id="display_name"
                              value={editForm.display_name}
                              onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                              className="glass border-white/20"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="bio">Bio</Label>
                            <Textarea
                              id="bio"
                              value={editForm.bio}
                              onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                              className="glass border-white/20"
                              rows={3}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="website">Sito Web</Label>
                            <Input
                              id="website"
                              value={editForm.website}
                              onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                              className="glass border-white/20"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="location">Posizione</Label>
                            <Input
                              id="location"
                              value={editForm.location}
                              onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                              className="glass border-white/20"
                            />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button variant="outline" onClick={() => setIsEditing(false)}>
                              Annulla
                            </Button>
                            <SecondaryButton onClick={handleSaveProfile}>
                              Salva
                            </SecondaryButton>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                     <GlassButton className="flex-shrink-0 p-2" onClick={handleShareProfile}>
                       {isSharing ? (
                         <Check className="w-4 h-4" />
                       ) : (
                         <Share className="w-4 h-4" />
                       )}
                     </GlassButton>
                     <Link to="/app/settings">
                       <GlassButton className="flex-shrink-0 p-2">
                         <Settings className="w-4 h-4" />
                       </GlassButton>
                     </Link>
                  </>
                 ) : (
                    <>
                      <SecondaryButton className="text-sm px-3 py-2 flex-shrink-0 whitespace-nowrap">
                        <span className="hidden sm:inline">Segui</span>
                        <span className="sm:hidden">+</span>
                      </SecondaryButton>
                      <GlassButton className="text-sm px-3 py-2 flex-shrink-0 whitespace-nowrap">
                        <span className="hidden sm:inline">Messaggio</span>
                        <span className="sm:hidden">💬</span>
                      </GlassButton>
                     <GlassButton className="flex-shrink-0 p-2" onClick={handleShareProfile}>
                       {isSharing ? (
                         <Check className="w-4 h-4" />
                       ) : (
                         <Share className="w-4 h-4" />
                       )}
                     </GlassButton>
                  </>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-4 justify-center md:justify-start">
              <div className="text-center">
                <p className="text-xl font-bold text-white">{formatCount(profile.posts_count)}</p>
                <p className="text-sm text-muted-foreground">Post</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-white">{formatCount(profile.pets_count)}</p>
                <p className="text-sm text-muted-foreground">Pet</p>
              </div>
              <Link 
                to={`/app/followers/${profile.username}`} 
                className="text-center hover:text-primary transition-colors cursor-pointer"
              >
                <p className="text-xl font-bold text-white">{formatCount(profile.followers_count)}</p>
                <p className="text-sm text-muted-foreground">Follower</p>
              </Link>
              <Link 
                to={`/app/following/${profile.username}`} 
                className="text-center hover:text-primary transition-colors cursor-pointer"
              >
                <p className="text-xl font-bold text-white">{formatCount(profile.following_count)}</p>
                <p className="text-sm text-muted-foreground">Seguiti</p>
              </Link>
            </div>

            {/* Bio and Info */}
            <div className="space-y-2">
              {profile.bio && (
                <p className="text-white">{profile.bio}</p>
              )}
              
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                {profile.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{profile.location}</span>
                  </div>
                )}
                {profile.website && (
                  <div className="flex items-center gap-1">
                    <LinkIcon className="w-4 h-4" />
                    <a 
                      href={profile.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {profile.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Iscritto da {format(new Date(profile.created_at), 'MMMM yyyy', { locale: it })}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfileHeader;